import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
  useRef
} from "react";
import { SiteContextValue } from "./types";
import { getSiteBaseUrl, initialMultiSiteContext, WP_API_PREFIX } from "./utils";
import { Box, CircularProgress } from "@mui/material";
import { baseThemeSx } from "../../utils";
import { useGetSiteIds } from "@hooks/use-get/site-ids";
import type { SubsiteInfo } from "@hooks/use-get/site-ids";
import { RootSignInView } from "../../views/sign-in-view/root-sign-in-view";

/**
 * Returns true when the current hostname has a subdomain prefix, e.g.
 *   school16.localhost  → true
 *   localhost           → false
 *   school.smassets.com → true
 *   smassets.com        → false
 */
const isOnSubdomain = (): boolean => {
  const parts = window.location.hostname.split(".");
  if (parts[parts.length - 1] === "localhost") return parts.length > 1;
  return parts.length > 2;
};

/** True when the current path is an OAuth provider callback (e.g. /auth/google/callback). */
const isAuthCallbackPath = (): boolean =>
  /^\/auth\/[^/]+\/callback(\?|$)/.test(window.location.pathname + window.location.search) ||
  /^\/auth\/[^/]+\/callback$/.test(window.location.pathname);

const MultiSiteContext = createContext<SiteContextValue>(
  initialMultiSiteContext
);

export const MultiSiteContextProvider = ({ children }: PropsWithChildren) => {
  const [state, setState] = useState<SiteContextValue>(initialMultiSiteContext);

  const { getSiteIds, loading } = useGetSiteIds();

  const hasFetchedIds = useRef(false);

  useEffect(() => {
    if (loading || hasFetchedIds.current) return;
    hasFetchedIds.current = true;

    const resolveSite = async () => {
      const hostname = window.location.hostname;
      const matchIn = (sites: SubsiteInfo[]) =>
        sites.find((s) => s.domain === hostname || s.slug === hostname.split(".")[0]);

      let sites: SubsiteInfo[] = [];
      try {
        sites = await getSiteIds();
      } catch (err) {
        console.error("Failed to fetch site IDs:", err);
      }

      let matched = matchIn(sites);
      // The subsite list is cached for 72h — on a match miss (e.g. a site was
      // just created/renamed on the backend since the cache was written),
      // refetch fresh once before concluding this hostname is really invalid,
      // otherwise a stale cache would bounce a valid site back to sign-in.
      if (!matched) {
        try {
          sites = await getSiteIds(true);
          matched = matchIn(sites);
        } catch (err) {
          console.error("Failed to refresh site IDs:", err);
        }
      }

      const isValid = !!matched;
      const baseUrl = isValid ? getSiteBaseUrl(matched!.slug) : "";
      setState({
        baseUrl,
        siteId: matched?.slug ?? hostname.split(".")[0],
        blogId: matched?.blog_id ?? null,
        valid: isValid,
        checked: true,
        getApiUrl: (pathSuffix: string): string => {
          // When no valid subdomain, target the WP network root directly.
          // An empty baseUrl would produce a relative URL hitting the dev server.
          const base = baseUrl || WP_API_PREFIX;
          return `${base}/${pathSuffix}`;
        },
      });
    };

    resolveSite();
  }, [loading, getSiteIds]);

  return (
    <MultiSiteContext.Provider value={state}>
      {!state.checked && (
        <Box
          sx={{
            ...baseThemeSx,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {state.checked && !state.valid && isOnSubdomain() && !isAuthCallbackPath() && (
        // Unknown subdomain — break the loop by sending the user back to the
        // root domain where RootSignInView can handle school selection.
        <Box
          sx={{
            ...baseThemeSx,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {(() => {
            const parts = window.location.hostname.split(".");
            const base = parts.slice(-(parts[parts.length - 1] === "localhost" ? 1 : 2)).join(".");
            const port = window.location.port ? `:${window.location.port}` : "";
            window.location.replace(`${window.location.protocol}//${base}${port}/sign-in`);
            return <CircularProgress />;
          })()}
        </Box>
      )}
      {state.checked && !state.valid && !isOnSubdomain() && !isAuthCallbackPath() && <RootSignInView />}
      {(state.valid || isAuthCallbackPath()) && children}
    </MultiSiteContext.Provider>
  );
};

export const useMultiSiteContext = () => {
  const context = useContext(MultiSiteContext);
  if (!context) {
    throw new Error(
      "useMultiSiteContext must be used within a MultiSiteContextProvider"
    );
  }
  return context;
};
